import { ApolloError, useMutation, FetchResult } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DesignerQuery_designer as Designer } from '../../generated/types/DesignerQuery';
import { Gender } from '../../generated/types/globalTypes';
import { reportError } from '../../lib/reportError';
import { useEmailVerification } from '../useEmailVerification';
import {
  DesignerEmailSubscribeMutationVariables,
  EmailSubscribeMutation,
  EmailSubscribeMutationVariables,
  EmailSubscribeMutationVariablesUnion,
  EMAIL_SUBSCRIBE_MUTATION
} from './useEmailSubscribe.rest';

export const useEmailSubscribe = () => {
  const { vertical = 'women' } = useParams<{
    vertical: Gender | undefined;
    trunkshowSlug: string | undefined;
    designerSlug: string | undefined;
  }>();

  const { isValid: isEmailValid, error: mailValidationError } = useEmailVerification();

  const [mutate, { loading, error }] = useMutation<
    EmailSubscribeMutation,
    EmailSubscribeMutationVariablesUnion
  >(EMAIL_SUBSCRIBE_MUTATION);

  const handleSuccess = useCallback(({ data }: FetchResult<EmailSubscribeMutation>) => {
    setIsSubscribed(true);
    setIsExistingSubscription(data?.emailSubscribe.table?.isExistingSubscription ?? false);
  }, []);

  const handleError = useCallback((caughtError: ApolloError) => reportError(caughtError), []);

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isExistingSubscription, setIsExistingSubscription] = useState(false);

  const subscribe = useCallback(
    (email: string, placement: 'footer' | 'emailPumoModule' | 'welcomePopup') => {
      if (!isEmailValid(email)) return;

      const variables: EmailSubscribeMutationVariables = {
        input: {
          user: {
            email,
            gender: Gender[vertical]
          },
          placement
        }
      };

      mutate({
        variables
      })
        .then(handleSuccess)
        .catch(handleError);
    },
    [handleError, handleSuccess, isEmailValid, mutate, vertical]
  );

  const subscribeToDesigner = useCallback(
    (email: string, designer: Designer) => {
      if (!isEmailValid(email)) return;

      const variables: DesignerEmailSubscribeMutationVariables = {
        input: {
          user: {
            email,
            gender: Gender[vertical]
          },
          placement: 'trunkshow',
          entity_id: Number(designer.id),
          entity_type: 'Trunkshow',
          trunkshow_preview: true
        }
      };

      mutate({
        variables
      })
        .then(handleSuccess)
        .catch(handleError);
    },
    [handleError, handleSuccess, isEmailValid, mutate, vertical]
  );

  return {
    subscribe,
    subscribeToDesigner,
    loading,
    error,
    mailValidationError,
    isSubscribed,
    isExistingSubscription
  };
};
